import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  TextField,
  Checkbox,
  FormControl,
  FormLabel,
  Typography,
  Paper,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  useEntityList,
  DefaultEntityFilters,
  EntityFilter,
} from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/lib/useAsync';
import { stringifyEntityRef, Entity } from '@backstage/catalog-model';

export class SpecDependsOnFilter implements EntityFilter {
  constructor(readonly refs: string[]) {}

  // Client-side fallback (keeps behavior correct even if server-side isn't applied)
  filterEntity(entity: Entity): boolean {
    if (!this.refs.length) return true;
    const deps: string[] = (entity as any).spec?.dependsOn ?? [];
    return this.refs.some(ref => deps.includes(ref));
  }

  // Server-side query used by the Catalog backend
  getCatalogFilters(): Record<string, string[] | string> {
    return this.refs.length ? { 'spec.dependsOn': this.refs } : {};
  }
}

type TechOption = { ref: string; label: string; tags: string[] };
type CustomFilters = DefaultEntityFilters & {
  projectTechRefs?: SpecDependsOnFilter;
};

const useStyles = makeStyles(theme =>
  createStyles({
    rootBox: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    label: {
      display: 'block',
      marginBottom: 6,
      fontWeight: 600,
      color: 'rgba(0,0,0,0.87)',
      fontSize: '0.875rem',
    },
    paper: {
      overflow: 'hidden',
      border: `1px solid ${theme.palette.divider}`,
    },
    topSection: {
      padding: theme.spacing(1.25),
      backgroundColor: theme.palette.action.hover,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    bottomSection: {
      padding: theme.spacing(1.25),
    },
    acRoot: {},
    acInputRoot: {
      // Tweak the text color & vertical padding
      '& input': {
        color: '#000',
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    acEndAdornment: {},
    acPopupIndicator: {
      color: '#616161',
      '& svg': { fontSize: 25 },
      '&:hover': { backgroundColor: 'transparent' },
    },
    acClearIndicator: {
      color: '#616161',
    },
    chipsRow: {
      marginTop: theme.spacing(1),
      marginBottom: -theme.spacing(0.75),
      marginRight: -theme.spacing(0.75),
      display: 'flex',
      flexWrap: 'wrap',
    },
    chip: {
      marginRight: theme.spacing(0.75),
      marginBottom: theme.spacing(0.75),
      fontWeight: 600,
    },
    tagChipsRow: {
      marginTop: theme.spacing(1),
      marginBottom: -theme.spacing(0.75),
      marginRight: -theme.spacing(0.75),
      display: 'flex',
      flexWrap: 'wrap',
    },
    tagChip: {
      marginRight: theme.spacing(0.75),
      marginBottom: theme.spacing(0.75),
      fontWeight: 500, // a bit lighter than tech chips
    },
  }),
);

export const ProjectTechnologyTagPicker = () => {
  const classes = useStyles();

  const {
    filters: { kind, projectTechRefs },
    updateFilters,
  } = useEntityList<CustomFilters>();
  const catalogApi = useApi(catalogApiRef);

  const isProject = String(kind?.value ?? '').toLowerCase() === 'project';

  // All Technology refs used by ANY Project (facets → no pagination gaps)
  const { value: allProjectTechRefs = [], loading: loadingRefs } =
    useAsync(async () => {
      if (!isProject) return [] as string[];
      const res = await catalogApi.getEntityFacets({
        facets: ['spec.dependsOn'],
        filter: { kind: 'Project' },
      });
      return (res.facets['spec.dependsOn'] ?? []).map((b: any) =>
        String(b.value).toLowerCase(),
      );
    }, [catalogApi, isProject]);

  // Resolve refs → label + tags (chunked)
  const { value: techOptions = [], loading: loadingTechs } =
    useAsync(async (): Promise<TechOption[]> => {
      if (!isProject || allProjectTechRefs.length === 0) return [];

      const chunk = <T,>(arr: T[], n: number) =>
        Array.from({ length: Math.ceil(arr.length / n) }, (_, i) =>
          arr.slice(i * n, i * n + n),
        );

      const chunks = chunk(allProjectTechRefs, 100);
      const out: TechOption[] = [];

      for (const batch of chunks) {
        const res = await catalogApi.getEntitiesByRefs({
          entityRefs: batch,
          fields: [
            'kind',
            'metadata.name',
            'metadata.namespace',
            'metadata.title',
            'spec.tags',
          ],
        });

        const items: TechOption[] = (res.items ?? [])
          .map((it: any) =>
            it && typeof it === 'object' && 'entity' in it ? it.entity : it,
          )
          .filter((e: any) => e && e.kind && e.metadata)
          .map((e: any) => ({
            ref: stringifyEntityRef(e).toLowerCase(),
            label:
              e.metadata?.title || e.metadata?.name || stringifyEntityRef(e),
            tags: Array.isArray(e.spec?.tags)
              ? e.spec.tags.map((t: any) => String(t))
              : [],
          }));

        out.push(...items);
      }

      const byRef = new Map<string, TechOption>();
      for (const it of out) byRef.set(it.ref, it);
      return Array.from(byRef.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      );
    }, [catalogApi, isProject, allProjectTechRefs]);

  // Build tag list ONLY from used technologies
  const tagOptions: string[] = useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of techOptions)
      for (const tag of t.tags) {
        counts.set(tag, (counts.get(tag) ?? 0) + 1);
      }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([tag]) => tag);
  }, [techOptions]);

  const [tagFilter, setTagFilter] = useState<string[]>([]);

  // Narrow tech options by selected tag(s) (UI-only)
  const filteredTechOptions = useMemo(() => {
    if (!tagFilter.length) return techOptions;
    return techOptions.filter(o => o.tags.some(t => tagFilter.includes(t)));
  }, [techOptions, tagFilter]);

  // Clear when leaving Projects (also clear UI tag filter)
  useEffect(() => {
    if (!isProject) {
      setTagFilter([]);
      if (projectTechRefs) updateFilters({ projectTechRefs: undefined });
    }
  }, [isProject, projectTechRefs, updateFilters]);

  // Keep tagFilter valid if tagOptions change
  useEffect(() => {
    setTagFilter(prev => prev.filter(t => tagOptions.includes(t)));
  }, [tagOptions]);

  if (!isProject || (!loadingRefs && !loadingTechs && techOptions.length === 0))
    return null;

  const selectedRefs = projectTechRefs?.refs ?? [];
  const selectedTechs = techOptions.filter(o => selectedRefs.includes(o.ref));

  return (
    <Box className={classes.rootBox}>
      <FormControl fullWidth>
        <FormLabel>
          <Typography className={classes.label}>
            Technologies with tags filtering
          </Typography>
        </FormLabel>

        <Paper className={classes.paper}>
          {/* Top section: Tag filter */}
          <Box className={classes.topSection}>
            <Typography
              variant="caption"
              style={{
                color: 'rgba(0,0,0,0.6)',
                display: 'block',
                marginBottom: 4,
              }}
            >
              Filter options
            </Typography>

            <Autocomplete
              multiple
              size="small"
              disableCloseOnSelect
              options={tagOptions}
              value={tagFilter}
              onChange={(_, newTags) =>
                setTagFilter((newTags as string[]) || [])
              }
              getOptionLabel={o => String(o)}
              getOptionSelected={(option, value) => option === value}
              renderOption={(option, { selected }) => (
                <>
                  <Checkbox checked={selected} style={{ marginRight: 8 }} />
                  {option}
                </>
              )}
              renderTags={() => null} // we don’t show chips inside the input here
              renderInput={params => (
                <TextField {...params} placeholder="Filter by tag…" />
              )}
              classes={{
                root: classes.acRoot,
                inputRoot: classes.acInputRoot,
                endAdornment: classes.acEndAdornment,
                popupIndicator: classes.acPopupIndicator,
                clearIndicator: classes.acClearIndicator,
              }}
              popupIcon={<KeyboardArrowDownRoundedIcon />}
            />

            {/* Chips UNDER the tag filter input */}
            {tagFilter.length > 0 && (
              <Box className={classes.tagChipsRow}>
                {tagFilter.map(label => (
                  <Chip
                    key={label}
                    label={label}
                    size="small"
                    variant="outlined"
                    className={classes.tagChip}
                    onDelete={() => {
                      setTagFilter(prev => prev.filter(t => t !== label));
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>

          {/* Bottom section: Technology picker + chips */}
          <Box className={classes.bottomSection}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={filteredTechOptions}
              loading={loadingRefs || loadingTechs}
              value={selectedTechs}
              getOptionLabel={o => o.label}
              getOptionSelected={(a, b) => a.ref === b.ref}
              renderTags={() => null} // chips below the input
              renderOption={(option, { selected }) => (
                <>
                  <Checkbox checked={selected} style={{ marginRight: 8 }} />
                  {option.label}
                </>
              )}
              renderInput={params => (
                <TextField {...params} placeholder="Search technologies…" />
              )}
              classes={{
                root: classes.acRoot,
                inputRoot: classes.acInputRoot,
                endAdornment: classes.acEndAdornment,
                popupIndicator: classes.acPopupIndicator,
                clearIndicator: classes.acClearIndicator,
              }}
              popupIcon={<KeyboardArrowDownRoundedIcon />}
              onChange={(_, newValue: TechOption[]) => {
                const refs = (newValue || []).map(v => v.ref);
                updateFilters({
                  projectTechRefs: refs.length
                    ? new SpecDependsOnFilter(refs)
                    : undefined,
                });
              }}
            />

            {/* Chips INSIDE the Paper, below input */}
            {selectedTechs.length > 0 && (
              <Box className={classes.chipsRow}>
                {selectedTechs.map(opt => (
                  <Chip
                    key={opt.ref}
                    label={opt.label}
                    size="small"
                    color="primary"
                    className={classes.chip}
                    onDelete={() => {
                      const next = selectedRefs.filter(r => r !== opt.ref);
                      updateFilters({
                        projectTechRefs: next.length
                          ? new SpecDependsOnFilter(next)
                          : undefined,
                      });
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Paper>
      </FormControl>
    </Box>
  );
};
